import React from 'react'
import PropTypes from 'prop-types'
import {Box, Divider, Flex, Skeleton, Text} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {format} from 'date-fns'

import {PdfContent} from './PdfContent/index'
import {AccountingProviderImportTag} from '../../AccountingProviderImportTag'
import {PayablePropType} from '../../../propTypes'

const PayableField = ({label, value, isLoading, textAlign, textStyle}) => (
  <Flex direction="column" textAlign={textAlign} justify="end" gap="xs">
    <Text textStyle="caption" color="mld.neutral.700">
      {label}
    </Text>
    {isLoading ? (
      <Skeleton width="100%" height="16px" />
    ) : (
      <Text textStyle={textStyle}>{value}</Text>
    )}
  </Flex>
)

PayableField.defaultProps = {
  textStyle: 'body-copy-medium',
  textAlign: 'right',
}

PayableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  textAlign: PropTypes.string,
  textStyle: PropTypes.string,
}

const formatPayableDate = (date) => {
  if (!date) return ''
  return format(new Date(date), 'dd MMM yyyy')
}

const PayableContentPanel = ({payable, isLoading}) => {
  const {t} = useTranslation('pointsOnPayments')

  return (
    <Flex direction="column" gap="md" overflow="visible" height="100%">
      <Flex
        justify="space-between"
        borderBottom="1px solid"
        borderColor="mld.neutral.900"
        pb="md"
      >
        <Flex direction="column" gap="sm">
          <AccountingProviderImportTag singular />
          <Skeleton isLoaded={!isLoading} minWidth="100%">
            <Text textStyle="headline4">{payable?.payeeName}</Text>
          </Skeleton>
        </Flex>
        <PayableField
          label={t(
            'pointsOnPayments:paymentWizard.payableContentPanel.balanceLabel',
          )}
          value={payable?.dueAmount?.toFormat()}
          textStyle="headline4"
          isLoading={isLoading}
        />
      </Flex>
      <Flex justify="space-between">
        <Box>
          {payable?.billNumber && (
            <PayableField
              label={t(
                'pointsOnPayments:paymentWizard.payableContentPanel.referenceLabel',
              )}
              value={payable?.billNumber}
              isLoading={isLoading}
              textAlign="left"
            />
          )}
        </Box>
        <Flex gap="sm">
          <PayableField
            label={t(
              'pointsOnPayments:paymentWizard.payableContentPanel.issueDateLabel',
            )}
            value={formatPayableDate(payable?.issueDate)}
            isLoading={isLoading}
          />
          <Divider orientation="vertical" />
          <PayableField
            label={t(
              'pointsOnPayments:paymentWizard.payableContentPanel.dueDateLabel',
            )}
            value={formatPayableDate(payable?.dueDate)}
            isLoading={isLoading}
          />
        </Flex>
      </Flex>
      <PdfContent payable={payable} isLoading={isLoading} />
    </Flex>
  )
}

PayableContentPanel.defaultProps = {
  payable: null,
}

PayableContentPanel.propTypes = {
  payable: PayablePropType,
  isLoading: PropTypes.bool.isRequired,
}

export default PayableContentPanel
